import { type ReactElement, useEffect, useState } from 'react';
import { getRootLayout } from '@/layout';
import { PRODUCT_HEADER_HEIGHT } from '@/shared/consts/productLayout.const';
import { useIsInExternalPlatform } from '@/shared/externalPlatform';
import { useIsSmallerThanTabletSm } from '@/shared/hooks/useIsSmallerThanTabletSm';
import { cn } from '@/utils/tailwind.util';
import { ProductBottomNavigation, ProductSidebar } from './ProductSidebar';
type ProductSidebarLayoutOptions = {
  /**
   * 모바일 하단 탭바(퀴즈 자료실·내 퀴즈·내 보드·내 클래스)를 숨긴다.
   * 자체 탭과 뒤로가기 동선을 가진 화면에서 탭바가 본문을 가리기만 할 때 쓴다.
   */
  hideMobileBottomNavigation?: boolean;
  /** 사이드바 없이 그릴 때(모바일·외부 플랫폼) main 의 여백을 비운다. 본문 여백을 페이지가 직접 두는 화면에서 쓴다. */
  hideContentPaddingWithoutSidebar?: boolean;
};
const ProductSidebarLayout = ({
  children,
  contentClassName,
  hideMobileBottomNavigation = false,
  hideContentPaddingWithoutSidebar = false
}: {
  children: ReactElement;
  contentClassName?: string;
} & ProductSidebarLayoutOptions) => {
  const [isMounted, setIsMounted] = useState(false);
  const isSmallerThanTabletSm = useIsSmallerThanTabletSm();
  const isInExternalPlatform = useIsInExternalPlatform();
  const shouldShowSidebar = isMounted && !isSmallerThanTabletSm && !isInExternalPlatform;
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // 마운트 뒤 모바일·외부 플랫폼이면 사이드바 없이 그린다. 두 모양 모두 `<main>{children}</main>` 을 같은
  // 자리에 둬야 한다 — 감싸는 요소가 바뀌면 React 가 페이지 전체를 다시 mount 해 effect 가 두 번 돈다
  // (예: 퀴즈 상세 조회 이벤트가 모바일에서 두 번 찍혔다).
  const isFlat = isMounted && !shouldShowSidebar;
  const showBottomNavigation = isFlat && isSmallerThanTabletSm && !isInExternalPlatform && !hideMobileBottomNavigation;
  return <div className={cn(!isFlat && 'flex w-full bg-default')} data-sentry-component="ProductSidebarLayout" data-sentry-source-file="ProductSidebarLayout.tsx">
      <div className={cn(!isFlat && 'flex w-full items-start bg-default')}>
        {!isFlat && <ProductSidebar
      // 마운트 전(서버 HTML·첫 렌더)엔 화면 폭을 모르므로 모바일에선 CSS 로 먼저 숨긴다.
      className="max-tablet-sm:hidden" style={{
        top: PRODUCT_HEADER_HEIGHT
      }} />}
        <main className={cn(isFlat ? [!hideContentPaddingWithoutSidebar && 'pt-md breakpoint-content-padding tablet-sm:pb-[80px] tablet-sm:pt-lg',
      // 하단 탭바 높이만큼 비워 두는 자리. 탭바를 숨기면 안전 영역만 남긴다.
      hideMobileBottomNavigation ? 'pb-[env(safe-area-inset-bottom)]' : 'pb-[calc(100px+env(safe-area-inset-bottom))]'] : ['min-w-0 flex-1 py-[12px] pb-[80px] pl-[24px] pr-[40px]',
      // 자체 하단 패딩을 가진 페이지(예: ClassroomDetailLayout)는 'pb-0' 로 중복 제거.
      contentClassName])}>
          {children}
        </main>
      </div>
      {showBottomNavigation && <ProductBottomNavigation />}
    </div>;
};
export function getProductSidebarLayout(page: ReactElement, contentClassName?: string, options?: ProductSidebarLayoutOptions) {
  return getRootLayout(<ProductSidebarLayout contentClassName={contentClassName} {...options}>
      {page}
    </ProductSidebarLayout>);
}